/**
 * The compact chrome variant of a guided operational flow
 * (`guidance.chrome: 'compact'`). The variant owns four pieces and nothing
 * else: a one-line header on the first step, the stepper with the revision
 * chip in one docked bar, the choice strip under it led by one figure, and the
 * back/forward pair docked to the bottom edge at every width. The standard
 * variant keeps the workspace header, the rail stepper, the choices panel and
 * the in-flow transition.
 */
import React from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { FmAccordion } from './FmAccordion';
import { FmButton } from './FmButton';
import { FmHeading } from './FmComposition';
import { OperationalWorkspaceStageNavigation } from './FmOperationalWorkspaceChrome';
import { guidedChoicesMade, guidedTransitionMoves } from './FmOperationalGuidance.helpers';
import { operationalWorkspaceStatus } from './FmOperationalWorkspaceStatus';
import type { FmDeviceTier } from './FmResponsive';
import { ReportNavBackAction } from './ReportNavBackAction';
import type {
  FmOperationalChoiceSummaryItem,
  FmOperationalCompactGuidanceDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';
import './FmOperationalGuidedChrome.css';

/**
 * One title line with the way back beside it. Shown on the first step only:
 * from the second step the docked bar names where the owner is, and the
 * header's height would push the step's work down every time.
 */
export function GuidedCompactHeader({
  descriptor,
  tier,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  tier: FmDeviceTier;
}): React.ReactElement {
  return (
    <header className="fm-guided-chrome__header" data-testid="guided-chrome-header">
      <div className="fm-guided-chrome__header-inner">
        {descriptor.returnAction ? (
          <ReportNavBackAction
            action={descriptor.returnAction}
            testId="operational-return-action"
          />
        ) : null}
        <FmHeading level={1} visualLevel={tier === 'mobile' ? 4 : 3}>
          {descriptor.title}
        </FmHeading>
      </div>
    </header>
  );
}

/**
 * The stepper and the revision chip in one docked bar. The chip carries the
 * same derived state the standard header's status rail does: the revision,
 * and the workspace status whenever it is anything other than ready, so a
 * save in flight or a conflict is never hidden by the compact chrome.
 */
export function GuidedDockBar({
  descriptor,
  activeStageId,
  stagesTestId,
  onStageChange,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStageId: string;
  stagesTestId: string;
  onStageChange: (stage: FmOperationalStageDescriptor) => void;
}): React.ReactElement {
  const status = operationalWorkspaceStatus(descriptor);
  const empty = descriptor.state === 'empty';
  return (
    <div className="fm-guided-chrome__bar">
      <OperationalWorkspaceStageNavigation
        descriptor={descriptor}
        activeStageId={activeStageId}
        stagesTestId={stagesTestId}
        onStageChange={onStageChange}
        variant="docked"
      />
      <p
        className="fm-guided-chrome__revision"
        data-status={status.key === 'ready' ? descriptor.revision.status : status.key}
      >
        <span
          className="fm-guided-chrome__revision-label"
          data-testid="operational-workspace-revision"
        >
          {empty ? descriptor.emptyLabel : descriptor.revision.label}
        </span>
        {status.key === 'ready' || empty ? null : (
          <span className="fm-guided-chrome__status" data-testid="operational-workspace-status">
            {status.label}
          </span>
        )}
      </p>
    </div>
  );
}

function ChoiceList({
  items,
  stages,
  changeLabel,
  readOnly,
  onStageChange,
}: {
  items: readonly FmOperationalChoiceSummaryItem[];
  stages: readonly FmOperationalStageDescriptor[];
  changeLabel: string;
  readOnly: boolean;
  onStageChange: (stageId: string) => void;
}): React.ReactElement {
  return (
    <dl className="fm-guided-chrome__choices">
      {items.map((item, itemIndex) => {
        const stage = stages.find((candidate) => candidate.id === item.stageId);
        // One way back per step: several choices from one step offer it on the first only.
        const firstOfStage =
          items.findIndex((other) => other.stageId === item.stageId) === itemIndex;
        const canChange = !readOnly && firstOfStage && stage && stage.status !== 'blocked';
        return (
          <div key={`${item.stageId}-${item.label}`} className="fm-guided-chrome__choice">
            <dt>{item.label}</dt>
            <dd>
              <span>{item.value}</span>
              {canChange ? (
                <FmButton
                  variant="text"
                  size="sm"
                  className="fm-guided-chrome__change"
                  aria-label={`${changeLabel}: ${item.label}`}
                  onClick={() => onStageChange(item.stageId)}
                >
                  {changeLabel}
                </FmButton>
              ) : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/**
 * The choices as the dock's second row. The lead figure opens it at figure
 * weight, every other choice follows as a quiet value with its way back. On a
 * phone the row is the lead figure and one disclosure; the choices open under
 * it, so none is clipped mid-value.
 */
export function GuidedSummaryStrip({
  guidance,
  stages,
  activeStageId,
  tier,
  testId,
  onStageChange,
}: {
  guidance: FmOperationalCompactGuidanceDescriptor;
  stages: readonly FmOperationalStageDescriptor[];
  activeStageId: string;
  tier: FmDeviceTier;
  testId: string;
  onStageChange: (stageId: string) => void;
}): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const summary = guidance.summary;
  const { items, lead } = guidedChoicesMade(guidance, stages, activeStageId);
  if (!summary || (!lead && !items.length)) return null;
  const leadFigure = lead ? (
    <span className="fm-guided-chrome__lead-figure">
      <span className="fm-guided-chrome__lead-value">{lead.value}</span>
      {lead.unit ? <span className="fm-guided-chrome__lead-unit">{lead.unit}</span> : null}
    </span>
  ) : null;
  const list = (
    <ChoiceList
      items={items}
      stages={stages}
      changeLabel={summary.changeLabel}
      readOnly={summary.readOnly === true}
      onStageChange={onStageChange}
    />
  );
  return (
    <section
      className="fm-guided-chrome__strip"
      aria-label={summary.title}
      data-testid={`${testId}-choices`}
    >
      {tier === 'mobile' && items.length ? (
        <FmAccordion
          plain
          type="single"
          open={open ? ['choices'] : []}
          onOpenChange={(keys) => setOpen(keys.includes('choices'))}
          data-testid={`${testId}-choices-disclosure`}
          items={[
            {
              key: 'choices',
              label: (
                <span className="fm-guided-chrome__disclosure">
                  {lead ? (
                    <span className="fm-guided-chrome__lead">
                      {leadFigure}
                      <span className="fm-guided-chrome__lead-label">{lead.label}</span>
                    </span>
                  ) : (
                    <span>{summary.title}</span>
                  )}
                  <span className="fm-guided-chrome__disclosure-action">
                    {open ? summary.collapseLabel : summary.expandLabel}
                  </span>
                </span>
              ),
              children: list,
            },
          ]}
        />
      ) : (
        <div className="fm-guided-chrome__strip-row">
          {lead ? (
            <div className="fm-guided-chrome__lead" data-testid={`${testId}-choices-lead`}>
              {leadFigure}
              <span className="fm-guided-chrome__lead-label">
                {lead.label}
                {lead.caption ? ` · ${lead.caption}` : ''}
              </span>
            </div>
          ) : null}
          {items.length ? list : null}
        </div>
      )}
    </section>
  );
}

/**
 * Back and forward docked to the bottom edge at every width. A forward move
 * the next step blocks stays in the bar, disabled, with the step's reason
 * beside it; the last step has no forward move, so its way back stays in the
 * flow instead of floating over the files.
 */
export function GuidedDockedTransition({
  guidance,
  stages,
  activeStage,
  onStageChange,
}: {
  guidance: FmOperationalCompactGuidanceDescriptor;
  stages: readonly FmOperationalStageDescriptor[];
  activeStage: FmOperationalStageDescriptor;
  onStageChange: (stageId: string) => void;
}): React.ReactElement | null {
  const { back, forward, blockedReason, sentence } = guidedTransitionMoves(
    guidance,
    stages,
    activeStage,
  );
  const reasonId = `${activeStage.id}-transition-reason`;
  if (!back && !forward && !sentence) return null;
  return (
    <div
      className="fm-guided-chrome__actions"
      data-final={forward ? undefined : ''}
      data-testid="guided-chrome-actions"
    >
      <div className="fm-guided-chrome__actions-inner">
        {sentence ? (
          <p
            id={blockedReason ? reasonId : undefined}
            data-blocked={blockedReason ? '' : undefined}
          >
            {sentence}
          </p>
        ) : null}
        <div className="fm-guided-chrome__moves">
          {back ? (
            <FmButton variant="bordered" size="lg" onClick={() => onStageChange(back.id)}>
              {guidance.previousLabel}
            </FmButton>
          ) : null}
          {forward ? (
            <FmButton
              variant="primary"
              size="lg"
              trailingIcon={<ArrowRightOutlined />}
              disabled={forward.status === 'blocked'}
              aria-describedby={blockedReason ? reasonId : undefined}
              onClick={() => onStageChange(forward.id)}
              data-testid={`guided-transition-${forward.id}`}
            >
              {activeStage.transitionActionLabel}
            </FmButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}
